import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, User, LogOut } from 'lucide-react';
import { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useNetworkStatus, useSyncQueueCount } from '@/hooks/useNetworkStatus';
import { OfflineIndicator } from '@/components/ui/offline-indicator';
import { cn } from '@/lib/utils';

const navigation = [
  { name: 'Dashboard', href: '/' },
  { name: 'Objekte', href: '/properties' },
  { name: 'Rechner', href: '/calculator' },
];

export function TopNav() {
  const location = useLocation();
  const { profile, signOut } = useAuthStore();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const isOnline = useNetworkStatus();
  const pendingCount = useSyncQueueCount();

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
      <nav className="max-w-content mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-foreground flex items-center justify-center">
              <Building2 className="w-4 h-4 text-background" />
            </div>
            <span className="font-semibold text-lg hidden sm:block">Fundament</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'relative px-4 py-2 text-sm font-medium rounded-full transition-colors',
                    isActive
                      ? 'text-foreground'
                      : 'text-foreground-muted hover:text-foreground'
                  )}
                >
                  {item.name}
                  {isActive && (
                    <motion.div
                      layoutId="nav-indicator"
                      className="absolute inset-0 bg-background-elevated rounded-full -z-10"
                      transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                </Link>
              );
            })}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-2">
            <OfflineIndicator isOnline={isOnline} pendingCount={pendingCount} />
            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center justify-center w-9 h-9 rounded-full bg-background-subtle hover:bg-background-elevated transition-colors text-sm font-medium"
              >
                {profile?.display_name?.[0]?.toUpperCase() || profile?.email?.[0]?.toUpperCase() || '?'}
              </button>

              {userMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setUserMenuOpen(false)}
                  />
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute right-0 top-full mt-2 w-56 bg-background-elevated border border-border rounded-lg shadow-lg z-50 overflow-hidden"
                  >
                    <div className="p-3 border-b border-border">
                      <p className="text-sm font-medium truncate">
                        {profile?.display_name || 'Benutzer'}
                      </p>
                      <p className="text-xs text-foreground-muted truncate">
                        {profile?.email}
                      </p>
                    </div>
                    <div className="p-1">
                      <Link
                        to="/settings"
                        className="flex items-center gap-2 px-3 py-2 text-sm text-foreground-muted hover:text-foreground hover:bg-background-subtle rounded-md"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <User className="w-4 h-4" />
                        Einstellungen
                      </Link>
                      <button
                        onClick={() => {
                          signOut();
                          setUserMenuOpen(false);
                        }}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-foreground-muted hover:text-foreground hover:bg-background-subtle rounded-md"
                      >
                        <LogOut className="w-4 h-4" />
                        Abmelden
                      </button>
                    </div>
                  </motion.div>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}
